
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetTicketByIdQuery,
  useReplyToTicketAdminMutation,
} from "@/features/api/supportApi";

import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import { Loader2, Send, Bell } from "lucide-react";
import { toast } from "sonner";

/* ================= UNREAD CHECK (ADMIN) ================= */
const hasUnreadUserMessage = (ticket) => {
  if (!ticket || !Array.isArray(ticket.replies)) return false;

  const last = ticket.replies[ticket.replies.length - 1];
  if (!last || last.repliedBy === "admin") return false;

  const lastSeen = localStorage.getItem(
    `support:lastSeenAdmin:${ticket._id}`
  );

  if (!lastSeen) return true;

  return (
    new Date(last.repliedAt).getTime() >
    new Date(lastSeen).getTime()
  );
};

const AdminReplyTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const [reply, setReply] = useState("");
  const prevMessageCount = useRef(0);

  /* ================= FETCH (AUTO REFRESH) ================= */
  const {
    data: ticket,
    isLoading,
    isError,
    refetch,
  } = useGetTicketByIdQuery(id, {
    skip: !id,
    pollingInterval: 5000,              // ✅ refresh every 5 sec
    refetchOnFocus: true,               // ✅ refetch when tab focused
    refetchOnReconnect: true,           // ✅ refetch if internet reconnects
  });

  const [sendReply, { isLoading: sending }] =
    useReplyToTicketAdminMutation();

  /* ================= MARK READ ================= */
  useEffect(() => {
    if (ticket?._id) {
      localStorage.setItem(
        `support:lastSeenAdmin:${ticket._id}`,
        new Date().toISOString()
      );
    }
  }, [ticket?._id]);

  /* ================= MESSAGES ================= */
  const messages = useMemo(() => {
    if (!ticket) return [];

    const base = [
      {
        id: "initial",
        from: "user",
        text: String(ticket.message || ""),
        time: ticket.createdAt || new Date(),
      },
    ];

    const replies = Array.isArray(ticket.replies)
      ? ticket.replies.map((r, i) => ({
          id: r._id || `${r.repliedAt}-${i}`,
          from: r.repliedBy === "admin" ? "admin" : "user",
          text: String(r.message || ""),
          time: r.repliedAt || new Date(),
        }))
      : [];

    return [...base, ...replies];
  }, [ticket]);

  /* ================= AUTO SCROLL (ONLY ON NEW MESSAGE) ================= */
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      prevMessageCount.current = messages.length;
    }
  }, [messages.length]);

  /* ================= SEND ================= */
  const handleSend = async () => {
    if (!reply.trim()) {
      toast.error("Reply message cannot be empty");
      return;
    }

    try {
      await sendReply({
        id,
        message: reply.trim(),
      }).unwrap();

      setReply("");
      toast.success("Reply sent");

      // ✅ immediately refresh after admin reply
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to send reply");
    }
  };

  /* ================= STATES ================= */
  if (isLoading) {
    return (
      <div className="flex justify-center mt-20">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="text-center mt-20 text-red-500">
        Ticket not found
      </div>
    );
  }

  const unread = hasUnreadUserMessage(ticket);

  /* ================= UI ================= */
  return (
    <div className="max-w-4xl mx-auto p-4 h-[85vh] flex flex-col">
      {/* HEADER */}
      <Card className="mb-3 p-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">
            🎫 {ticket.subject || "Support Ticket"}
          </h2>

          {unread && (
            <span className="flex items-center gap-1 text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
              <Bell size={12} /> New
            </span>
          )}
        </div>

        <Button
          variant="outline"
          onClick={() => navigate("/admin/support/list")}
        >
          ← Back
        </Button>
      </Card>

      {/* CHAT */}
      <div className="flex-1 overflow-y-auto bg-gray-100 rounded-lg p-4 space-y-3">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${
              m.from === "admin"
                ? "justify-end"
                : "justify-start"
            }`}
          >
            <div
              className={`max-w-[70%] px-4 py-2 rounded-xl text-sm shadow ${
                m.from === "admin"
                  ? "bg-green-500 text-white"
                  : "bg-white text-gray-800"
              }`}
            >
              <p className="whitespace-pre-wrap">{m.text}</p>
              <div className="text-[10px] opacity-60 text-right">
                {new Date(m.time).toLocaleString()}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      {ticket.status !== "solved" && (
        <div className="mt-3 flex gap-2">
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply..."
            rows={2}
          />
          <Button onClick={handleSend} disabled={sending}>
            {sending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminReplyTicket;
